/**
 * Inline preview glyphs for compound structures (fractions, sqrt,
 * matrices, …). Two render modes:
 *
 *   1. **Static preview** (Structures tab tiles) — built from the token
 *      factories and rendered with no `ctx`, so slots show as empty
 *      placeholder boxes (or their sample content). No interaction.
 *   2. **Live token** (inside the equation, via `renderStructure` with a
 *      `SlotRenderCtx`) — each slot is a clickable, recursively-rendered
 *      mini-equation. A slot can therefore hold atoms, symbols, AND
 *      nested structures. The focused slot (per the editor's focus path)
 *      gets a purple ring and a blinking caret; clicking any slot routes
 *      both typing and palette inserts into that slot's token array.
 *
 * Slots are addressed by a `FocusPath`: the path to this structure's
 * container plus `{ tokenIdx: selfIdx, slotKey }`. Rendering recurses —
 * a slot renders its tokens, each nested structure renders its own
 * slots, and so on, with the path extended at each hop.
 */
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { CAT_COLORS } from "./palette";
import { samePath, T, type FocusPath, type StructToken, type Token } from "./tokens";

const wrapper: CSSProperties = { display: "inline-flex", verticalAlign: "middle" };

/** Live-mode render context, threaded down through every slot. */
export interface SlotRenderCtx {
  /** Absolute focus path of the slot the caret is currently in. */
  focusPath: FocusPath;
  /** Caret index within the focused container. */
  cursor: number;
  /** Display font size in px (top-level); nested slots scale via em. */
  displaySize: number;
  /** When true, category-inked atoms are tinted. */
  blockColors: boolean;
  /** Focus a slot by its path (editor moves the caret to its end). */
  onSlotClick?: (path: FocusPath) => void;
}

interface GlyphProps {
  /** The structure being rendered. */
  token: StructToken;
  /** Path to this structure's container ([] at the top level). */
  basePath: FocusPath;
  /** Index of this structure within its container. */
  selfIdx: number;
  /** Live context; absent for static previews. */
  ctx?: SlotRenderCtx;
}

/** A blinking caret bar that takes no horizontal space in the flow. */
function CaretBar({ on }: { on: boolean }) {
  return (
    <span style={{ display: "inline-block", width: 0, position: "relative", verticalAlign: "middle" }}>
      {on && (
        <span
          style={{
            position: "absolute",
            top: "-0.55em",
            height: "1.1em",
            left: 0,
            width: 1.5,
            background: "var(--lilia-primary)",
            animation: "mc-caret 1s steps(1) infinite",
            borderRadius: 1,
          }}
        />
      )}
    </span>
  );
}

/** Render one atom (or nested structure) inside a slot. */
function InlineToken({ token, basePath, idx, ctx }: {
  token: Token; basePath: FocusPath; idx: number; ctx?: SlotRenderCtx;
}): ReactNode {
  if (token.kind === "struct") {
    return renderStructure(token as StructToken, basePath, idx, ctx);
  }
  // newline/align aren't expected inside slots — render nothing.
  if (token.kind === "newline" || token.kind === "align") return null;
  const ink = ctx?.blockColors && token.cat ? CAT_COLORS[token.cat].ink : undefined;
  const style: CSSProperties = {
    fontStyle: token.italic ? "italic" : "normal",
    color: ink,
    margin: token.mx ? `0 ${(token.mx * 0.32).toFixed(3)}em` : 0,
  };
  if (token.kind === "bigop") {
    return (
      <span style={{ ...style, fontSize: `${token.size ?? 1.4}em`, lineHeight: 1, verticalAlign: "middle" }}>
        {token.g}
      </span>
    );
  }
  return <span style={style}>{token.g}</span>;
}

/** Render a slot's token list inline, with carets when it's focused. */
function renderInline(tokens: Token[], basePath: FocusPath, ctx?: SlotRenderCtx): ReactNode {
  const focused = !!ctx && samePath(ctx.focusPath, basePath);
  const nodes: ReactNode[] = [];
  if (focused) nodes.push(<CaretBar key="c-0" on={ctx!.cursor === 0} />);
  tokens.forEach((t, j) => {
    nodes.push(<InlineToken key={`t-${j}`} token={t} basePath={basePath} idx={j} ctx={ctx} />);
    if (focused) nodes.push(<CaretBar key={`c-${j + 1}`} on={ctx!.cursor === j + 1} />);
  });
  return nodes;
}

interface SlotProps {
  slotKey: string;
  value: Token[];
  /** Absolute path of this slot. */
  path: FocusPath;
  ctx?: SlotRenderCtx;
  /** Visual size scaling (smaller for sub/sup vs the main slot). */
  small?: boolean;
  /**
   * Optional slots (e.g. a sub/sup script) don't paint a placeholder
   * box when they're empty and unfocused, so `a^2` shows just the
   * superscript with no dangling empty sub/base boxes. The slot is
   * still reachable via Tab (which focuses it → the box reappears).
   */
  optional?: boolean;
}

/**
 * One editable slot. Empty → dashed-purple box; non-empty → its tokens
 * rendered recursively; focused → purple ring + caret.
 */
function Slot({ slotKey, value, path, ctx, small, optional }: SlotProps) {
  const empty = value.length === 0;
  const focused = !!ctx && samePath(ctx.focusPath, path);
  const interactive = !!ctx?.onSlotClick;

  // Collapse an empty optional script slot until it's focused — but
  // only in live mode (ctx present). Static previews keep the boxes so
  // the Structures-tab tile still communicates the structure's shape.
  if (empty && optional && !focused && ctx) return null;
  const handleClick = interactive
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        ctx!.onSlotClick!(path);
      }
    : undefined;

  if (empty) {
    return (
      <span
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={handleClick}
        data-slot-key={slotKey}
        data-slot-empty="true"
        style={{
          display: "inline-block",
          width: small ? 8 : 14,
          height: small ? 10 : 16,
          minWidth: small ? 8 : 14,
          border: `1px dashed ${focused ? "var(--lilia-primary)" : "color-mix(in srgb, var(--lilia-primary) 40%, transparent)"}`,
          borderRadius: 2,
          background: focused ? "color-mix(in srgb, var(--lilia-primary) 18%, transparent)" : "color-mix(in srgb, var(--lilia-primary) 6%, transparent)",
          boxShadow: focused ? "0 0 0 2px color-mix(in srgb, var(--lilia-primary) 30%, transparent)" : "none",
          verticalAlign: "middle",
          cursor: interactive ? "text" : "default",
          position: "relative",
        }}
      >
        {focused && (
          <span
            style={{
              position: "absolute",
              top: 2,
              bottom: 2,
              left: 2,
              width: 1.5,
              background: "var(--lilia-primary)",
              animation: "mc-caret 1s steps(1) infinite",
            }}
          />
        )}
      </span>
    );
  }

  return (
    <span
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={handleClick}
      data-slot-key={slotKey}
      style={{
        padding: "0 2px",
        borderRadius: 2,
        background: focused ? "color-mix(in srgb, var(--lilia-primary) 14%, transparent)" : "transparent",
        boxShadow: focused ? "0 0 0 1.5px color-mix(in srgb, var(--lilia-primary) 45%, transparent)" : "none",
        cursor: interactive ? "text" : "default",
      }}
    >
      {renderInline(value, path, ctx)}
    </span>
  );
}

/** Build the React node for slot `key` of the structure in `p`. */
function slotNode(key: string, p: GlyphProps, opts?: { small?: boolean; optional?: boolean }): ReactNode {
  const value = p.token.slots[key] ?? [];
  const path: FocusPath = [...p.basePath, { tokenIdx: p.selfIdx, slotKey: key }];
  return <Slot slotKey={key} value={value} path={path} ctx={p.ctx} small={opts?.small} optional={opts?.optional} />;
}

export function FracGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, flexDirection: "column", alignItems: "center", margin: "0 2px" }}>
      <span style={{ padding: "0 4px" }}>{slotNode("a", p)}</span>
      <span style={{ borderTop: "1.5px solid currentColor", alignSelf: "stretch", margin: "1px 0" }} />
      <span style={{ padding: "0 4px" }}>{slotNode("b", p)}</span>
    </span>
  );
}

export function SqrtGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "stretch" }}>
      <span style={{ fontSize: "1.6em", lineHeight: 0.9, marginRight: -2 }}>√</span>
      <span style={{ borderTop: "1.5px solid currentColor", padding: "2px 4px 0" }}>{slotNode("a", p)}</span>
    </span>
  );
}

export function NthRootGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "flex-start" }}>
      <sup style={{ fontSize: "0.55em", marginRight: -4, marginTop: 2 }}>{slotNode("n", p, { small: true })}</sup>
      <span style={{ fontSize: "1.6em", lineHeight: 0.9, marginRight: -2 }}>√</span>
      <span style={{ borderTop: "1.5px solid currentColor", padding: "2px 4px 0" }}>{slotNode("a", p)}</span>
    </span>
  );
}

export function SubSupGlyph(p: GlyphProps) {
  // Base + scripts are all optional: an empty, unfocused slot paints no
  // box, so `a^2` reads as a clean superscript (no leftover sub/base box).
  return (
    <span style={{ verticalAlign: "middle" }}>
      {slotNode("b", p, { optional: true })}
      <sup style={{ fontSize: "0.62em" }}>{slotNode("sup", p, { small: true, optional: true })}</sup>
      <sub style={{ fontSize: "0.62em", marginLeft: -2 }}>{slotNode("sub", p, { small: true, optional: true })}</sub>
    </span>
  );
}

export function IntegralGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ position: "relative", display: "inline-block", height: "1.4em", marginRight: 2 }}>
        <span style={{ fontSize: "1.8em", lineHeight: 1, fontWeight: 300 }}>∫</span>
        <span style={{ position: "absolute", right: -4, top: -2, fontSize: "0.5em" }}>{slotNode("hi", p, { small: true })}</span>
        <span style={{ position: "absolute", right: 0, bottom: -2, fontSize: "0.5em" }}>{slotNode("lo", p, { small: true })}</span>
      </span>
      <span style={{ fontSize: "0.85em" }}>{slotNode("body", p)}</span>
    </span>
  );
}

export function SumGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ position: "relative", display: "inline-block", height: "1.4em" }}>
        <span style={{ fontSize: "1.5em", lineHeight: 1 }}>∑</span>
        <span style={{ position: "absolute", left: 0, right: 0, top: -2, textAlign: "center", fontSize: "0.45em" }}>{slotNode("hi", p, { small: true })}</span>
        <span style={{ position: "absolute", left: 0, right: 0, bottom: -2, textAlign: "center", fontSize: "0.45em" }}>{slotNode("lo", p, { small: true })}</span>
      </span>
      <span style={{ fontSize: "0.85em", marginLeft: 4 }}>{slotNode("body", p)}</span>
    </span>
  );
}

export function LimGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
        <span style={{ fontStyle: "normal", fontSize: "0.85em" }}>lim</span>
        <span style={{ fontSize: "0.5em", marginTop: 1 }}>{slotNode("target", p, { small: true })}</span>
      </span>
      <span style={{ fontSize: "0.85em", marginLeft: 4 }}>{slotNode("body", p)}</span>
    </span>
  );
}

export function MatrixGlyph(p: GlyphProps) {
  const r = p.token.rows ?? 2;
  const c = p.token.cols ?? 2;
  const cells: ReactNode[] = [];
  for (let i = 0; i < r; i++) {
    for (let j = 0; j < c; j++) {
      const key = `r${i}c${j}`;
      cells.push(
        <span key={key} style={{ textAlign: "center" }}>{slotNode(key, p)}</span>,
      );
    }
  }
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.7em", lineHeight: 0.7 }}>(</span>
      <span
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${c}, auto)`,
          gap: "3px 8px",
          padding: "2px 4px",
          fontSize: "0.85em",
        }}
      >
        {cells}
      </span>
      <span style={{ fontSize: "1.7em", lineHeight: 0.7 }}>)</span>
    </span>
  );
}

export function CasesGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.8em", lineHeight: 0.7 }}>{"{"}</span>
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, fontSize: "0.85em", marginLeft: 4 }}>
        <span>
          {slotNode("a", p)}
          &nbsp;&nbsp;
          {slotNode("a_cond", p)}
        </span>
        <span>
          {slotNode("b", p)}
          &nbsp;&nbsp;
          {slotNode("b_cond", p)}
        </span>
      </span>
    </span>
  );
}

export function AlignedGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, flexDirection: "column", fontSize: "0.85em", gap: 2 }}>
      <span>
        {slotNode("lhs1", p)} = {slotNode("rhs1", p)}
      </span>
      <span>
        &nbsp;&nbsp; = {slotNode("rhs2", p)}
      </span>
    </span>
  );
}

export function VectorGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.9em", lineHeight: 0.65 }}>(</span>
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, padding: "2px 4px", fontSize: "0.85em" }}>
        <span>{slotNode("a", p)}</span>
        <span>{slotNode("b", p)}</span>
        <span>{slotNode("c", p)}</span>
      </span>
      <span style={{ fontSize: "1.9em", lineHeight: 0.65 }}>)</span>
    </span>
  );
}

/**
 * Absolute value bars wrapping a single editable slot. The bars are
 * sized to match the slot height, like the LaTeX `\left|…\right|`
 * pair would when typeset.
 */
export function AbsGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.3em", lineHeight: 1, padding: "0 1px" }}>|</span>
      <span style={{ padding: "0 3px", fontSize: "0.95em" }}>{slotNode("body", p)}</span>
      <span style={{ fontSize: "1.3em", lineHeight: 1, padding: "0 1px" }}>|</span>
    </span>
  );
}

/**
 * Parenthesised group — emits `\left( … \right)` so the parens
 * stretch with their contents. Same single-slot shape as `abs`.
 */
const DELIM_GLYPHS: Record<string, [string, string]> = {
  paren: ["(", ")"], bracket: ["[", "]"], brace: ["{", "}"], angle: ["⟨", "⟩"],
};

export function ParensGlyph(p: GlyphProps) {
  const [lo, ro] = DELIM_GLYPHS[p.token.delim ?? "paren"] ?? DELIM_GLYPHS.paren;
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.3em", lineHeight: 1, padding: "0 1px" }}>{lo}</span>
      <span style={{ padding: "0 3px", fontSize: "0.95em" }}>{slotNode("body", p)}</span>
      <span style={{ fontSize: "1.3em", lineHeight: 1, padding: "0 1px" }}>{ro}</span>
    </span>
  );
}

/**
 * Accent mark over a single base slot. `bar`/`overline` draw a rule over
 * the base; the rest float a small mark centred above it.
 */
export function AccentGlyph(p: GlyphProps) {
  const a = p.token.accent ?? "hat";
  const base = slotNode("base", p);
  if (a === "bar" || a === "overline") {
    return (
      <span style={{ display: "inline-block", borderTop: "1.5px solid currentColor", paddingTop: 1, lineHeight: 1 }}>
        {base}
      </span>
    );
  }
  const mark =
    a === "vec" || a === "overrightarrow" ? "→"
    : a === "tilde" || a === "widetilde" ? "~"
    : a === "dot" ? "˙"
    : a === "ddot" ? "¨"
    : a === "check" ? "ˇ"
    : a === "acute" ? "´"
    : a === "grave" ? "`"
    : "^"; // hat / widehat / default
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1, verticalAlign: "middle" }}>
      <span style={{ fontSize: "0.7em", height: "0.5em", marginBottom: -1 }}>{mark}</span>
      <span>{base}</span>
    </span>
  );
}

/**
 * Binomial coefficient — numerator over denominator (no rule), wrapped in
 * tall parentheses. Same two-slot shape as a fraction.
 */
export function BinomGlyph(p: GlyphProps) {
  return (
    <span style={{ ...wrapper, alignItems: "center" }}>
      <span style={{ fontSize: "1.9em", lineHeight: 0.65 }}>(</span>
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", padding: "0 4px" }}>
        <span>{slotNode("a", p)}</span>
        <span>{slotNode("b", p)}</span>
      </span>
      <span style={{ fontSize: "1.9em", lineHeight: 0.65 }}>)</span>
    </span>
  );
}

/**
 * Render a structure. `basePath`/`selfIdx` locate it in the tree so its
 * slots get correct absolute paths; `ctx` (when present) makes the slots
 * live (clickable + caret). Recurses for nested structures.
 */
export function renderStructure(
  token: StructToken,
  basePath: FocusPath = [],
  selfIdx = 0,
  ctx?: SlotRenderCtx,
): ReactElement {
  const p: GlyphProps = { token, basePath, selfIdx, ctx };
  switch (token.structKind) {
    case "frac":     return <FracGlyph {...p} />;
    case "sqrt":     return <SqrtGlyph {...p} />;
    case "nthroot":  return <NthRootGlyph {...p} />;
    case "subsup":   return <SubSupGlyph {...p} />;
    case "integral": return <IntegralGlyph {...p} />;
    case "sum":      return <SumGlyph {...p} />;
    case "lim":      return <LimGlyph {...p} />;
    case "matrix":   return <MatrixGlyph {...p} />;
    case "cases":    return <CasesGlyph {...p} />;
    case "aligned":  return <AlignedGlyph {...p} />;
    case "vector":   return <VectorGlyph {...p} />;
    case "abs":      return <AbsGlyph {...p} />;
    case "parens":   return <ParensGlyph {...p} />;
    case "accent":   return <AccentGlyph {...p} />;
    case "binom":    return <BinomGlyph {...p} />;
    default:         return <span>?</span>;
  }
}

/**
 * Static preview used by the Structures tab tiles. Built from the token
 * factories and rendered with no `ctx`, so empty slots show as
 * placeholder boxes (communicating "this has slots you fill in").
 */
export const STRUCT_PREVIEWS: Record<StructToken["structKind"], () => ReactElement> = {
  frac:     () => <FracGlyph token={T.frac()} basePath={[]} selfIdx={0} />,
  sqrt:     () => <SqrtGlyph token={T.sqrt()} basePath={[]} selfIdx={0} />,
  nthroot:  () => <NthRootGlyph token={T.nthroot()} basePath={[]} selfIdx={0} />,
  subsup:   () => <SubSupGlyph token={T.subsup("x")} basePath={[]} selfIdx={0} />,
  integral: () => <IntegralGlyph token={T.integral()} basePath={[]} selfIdx={0} />,
  sum:      () => <SumGlyph token={T.sumStruct()} basePath={[]} selfIdx={0} />,
  lim:      () => <LimGlyph token={T.limStruct()} basePath={[]} selfIdx={0} />,
  matrix:   () => <MatrixGlyph token={T.matrix(2, 2)} basePath={[]} selfIdx={0} />,
  cases:    () => <CasesGlyph token={T.cases()} basePath={[]} selfIdx={0} />,
  aligned:  () => <AlignedGlyph token={T.aligned()} basePath={[]} selfIdx={0} />,
  vector:   () => <VectorGlyph token={T.vector()} basePath={[]} selfIdx={0} />,
  abs:      () => <AbsGlyph token={T.abs("x")} basePath={[]} selfIdx={0} />,
  parens:   () => <ParensGlyph token={T.parens("x")} basePath={[]} selfIdx={0} />,
  accent:   () => <AccentGlyph token={T.accent("hat", "x")} basePath={[]} selfIdx={0} />,
  binom:    () => <BinomGlyph token={T.binom()} basePath={[]} selfIdx={0} />,
};
